import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Form, Button, Divider } from "antd";
import { useIntl } from "react-intl";

import Collis from "./collis";
import Product from "./product";
import Services from "./services";
import AddressRow from "./addressRow";
import {
  post,
  checkHttpStatus,
  checkResponseGet,
} from "../../../modules/httpFunctions";
import { error } from "../../../modules/messages";
import { handleError } from "../../../modules/errorLogger";
import { loadCountries } from "../../../modules/staticDataLoad";
import { showMessageIfExists } from "../../../modules/crudOperations";
import { loadClientRules } from "../../../modules/consignmentFunctions";

import "./index.css";

const notEu = (country) => country && !country.europeanUnion;
const notNorthIreland = (country, zip) =>
  !country || country.code !== "GB" || !zip || !zip.startsWith("BT");

function CalculationForm({
  form,
  onSetNonEu,
  onSetResult,
  onSetLoading,
  onSetFormData,
  onSetIsSlovakia,
}) {
  const intl = useIntl();
  const { getFieldDecorator } = form;
  const formItemLayout = { colon: false };

  const handleAnyValueChange = useCallback(() => onSetResult(), [onSetResult]);

  const [adr, setAdr] = useState(false);
  const [formData, setFormData] = useState();
  const handleSetAdr = useCallback(
    (e) => {
      setFormData();
      handleAnyValueChange();
      setAdr(e.target.checked);
    },
    [handleAnyValueChange]
  );

  const [hasServices, setHasServices] = useState(false);

  const [czech, setCzech] = useState();
  const [products, setProducts] = useState();
  const [countries, setCountries] = useState();
  const [pickupZip, setPickupZip] = useState();
  const [deliveryZip, setDeliveryZip] = useState();
  const [pickupCountry, setPickupCountry] = useState();
  const [deliveryCountry, setDeliveryCountry] = useState();
  const [selectedProduct, setSelectedProduct] = useState();
  const [selectCountries, setSelectCountries] = useState([]);
  const handleDeliveryCountryChange = useCallback((country) => {
    onSetIsSlovakia(country?.code === "SK");
    setDeliveryCountry(country);
  }, [onSetIsSlovakia]);
  const handleLoadData = useCallback(() => {
    loadCountries((loadedCountries) => {
      if (loadedCountries && loadedCountries !== "e") {
        const country = loadedCountries.find((c) => c.code === "CZ");
        setCzech(country);
        setCountries(loadedCountries);
        setPickupCountry(country);
        handleDeliveryCountryChange(country);
      }
    });
  }, [handleDeliveryCountryChange]);

  const [rules, setRules] = useState({});
  const [carryToFloor, setCarryToFloor] = useState(false);
  useEffect(() => {
    loadClientRules(
      selectedProduct,
      carryToFloor,
      setRules,
      undefined,
      pickupCountry && pickupCountry.Id,
      deliveryCountry && deliveryCountry.id
    );
  }, [selectedProduct, carryToFloor, pickupCountry, deliveryCountry]);

  const [collis, setCollis] = useState([]);
  const [validCollis, setValidCollis] = useState();
  const [loading, setLoading] = useState();
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      form &&
        form.validateFields((err, values) => {
          if (!err) {
            const collis = [];
            for (let i = 0; i < values.counts.length; i++) {
              const count = values.counts[i] || 1;
              for (let j = 0; j < count; j++) {
                collis.push({
                  height: values.heights[i],
                  length: values.lengths[i],
                  width: values.widths[i],
                  weight: values.weights[i],
                  stackable: values.stackables[i],
                  wrappingId: values.wrappings[i],
                  loadingMeters:
                    values.loadingMeters && values.loadingMeters.length > i
                      ? values.loadingMeters[i] / count
                      : undefined,
                });
              }
            }
            setValidCollis();
            setFormData({
              ...values,
              payByCreditCard:
                selectedProduct &&
                selectedProduct.domestic &&
                values.cashOnDeliveryAmount > 0,
              collis,
            });
          } else {
            setFormData();
          }
        });
    },
    [form, selectedProduct]
  );

  useEffect(() => {
    if (formData) {
      if (validCollis) {
        setLoading(true);
        onSetFormData(formData);
        post("calculation", formData)
          .then((r) => {
            if (checkHttpStatus(r) && checkResponseGet(r)) {
              onSetResult(r.data.hardSuccess ? r.data.message : r.data.data);
            } else {
              showMessageIfExists(r);
              onSetResult("e");
            }
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
            error(
              intl.formatMessage({
                id: "calculator.error",
                defaultMessage: "Při výpočtu ceny nastala chyba!",
              }),
              10
            );
            handleError(err);
          });
      } else if (validCollis === false) {
        onSetResult("v");
      }
    }
  }, [intl, validCollis, formData, onSetResult, onSetFormData]);

  useEffect(() => {
    handleLoadData();
  }, [intl.locale, handleLoadData]);

  useEffect(
    () =>
      countries &&
      setSelectCountries(
        selectedProduct &&
          selectedProduct.countries &&
          selectedProduct.countries.length > 0
          ? selectedProduct.countries
          : [czech]
      ),
    [czech, countries, selectedProduct]
  );

  useEffect(() => {
    onSetNonEu(
      (notEu(pickupCountry) || notEu(deliveryCountry)) &&
        notNorthIreland(pickupCountry, pickupZip) &&
        notNorthIreland(deliveryCountry, deliveryZip)
    );
  }, [onSetNonEu, pickupZip, pickupCountry, deliveryZip, deliveryCountry]);

  return (
    <Form {...formItemLayout}>
      <Product
        form={form}
        czech={czech}
        collis={collis}
        products={products}
        onSetProducts={setProducts}
        onSetLoading={onSetLoading}
        formItemLayout={formItemLayout}
        getFieldDecorator={getFieldDecorator}
        onAnyValueChange={handleAnyValueChange}
        onSetSelectedProduct={setSelectedProduct}
      />
      <Divider />
      <Row gutter={50}>
        <AddressRow
          pickup
          form={form}
          czech={czech}
          onZipChange={setPickupZip}
          formItemLayout={formItemLayout}
          selectedProduct={selectedProduct}
          selectCountries={selectCountries}
          onCountrySelect={setPickupCountry}
          getFieldDecorator={getFieldDecorator}
          onAnyValueChange={handleAnyValueChange}
        />
        <AddressRow
          form={form}
          czech={czech}
          onZipChange={setDeliveryZip}
          formItemLayout={formItemLayout}
          selectedProduct={selectedProduct}
          selectCountries={selectCountries}
          getFieldDecorator={getFieldDecorator}
          onAnyValueChange={handleAnyValueChange}
          onCountrySelect={handleDeliveryCountryChange}
        />
      </Row>
      <Divider />
      <Collis
        adr={adr}
        form={form}
        rules={rules}
        formData={formData}
        products={products}
        onSetCollis={setCollis}
        validCollis={validCollis}
        onSetFormData={setFormData}
        formItemLayout={formItemLayout}
        selectedProduct={selectedProduct}
        onSetValidCollis={setValidCollis}
        getFieldDecorator={getFieldDecorator}
        onAnyValueChange={handleAnyValueChange}
      />
      {hasServices && <Divider />}
      <Services
        rules={rules}
        onAdrChange={handleSetAdr}
        pickupCountry={pickupCountry}
        formItemLayout={formItemLayout}
        deliveryCountry={deliveryCountry}
        selectedProduct={selectedProduct}
        onSetHasServices={setHasServices}
        onSetCarryToFloor={setCarryToFloor}
        getFieldDecorator={getFieldDecorator}
        onAnyValueChange={handleAnyValueChange}
      />
      <Divider />
      <Row type="flex" justify="center">
        <Col>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {intl.formatMessage({
              id: "messages.calculate",
              defaultMessage: "Spočítat",
            })}
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

export default Form.create()(CalculationForm);
